import {
	createRouter,
	createWebHistory,
	isNavigationFailure,
	NavigationFailureType,
} from "vue-router";
import { can, type Role } from "@/features/auth/api/can";
import { markSessionSettled, restoreSession, useSessionState } from "@/features/auth/api/session";
import { partnerGuardResult } from "@/features/partner/api/partner-session";
import { readPlatformAccess } from "@/features/platform/api/use-platform";
import type { AccessModule } from "@/shared/access/modules";
import { moduleForPath } from "@/shared/access/modules";
import { request } from "@/shared/http/client";
import { beginLoading } from "@/shared/http/loading-bar";
import { newDeployIsWaiting } from "./deploy-watch";
import { syncManifestLink } from "./pwa-manifest";
import { routes } from "./routes";

/**
 * O router: onde se entra, e quem pode.
 *
 * A LISTA de rotas mora em `routes.ts`. Ela cresce com cada tela e a guarda não
 * cresce com nenhuma — mantê-las no mesmo arquivo era ler 250 linhas de destinos
 * para achar as vinte que decidem quem passa. Aqui ficou só a decisão.
 */
export const router = createRouter({
	history: createWebHistory(),
	routes,
	// Every navigation starts at the top. Carrying the previous page's scroll into
	// a different screen is disorienting in a way nobody can name.
	scrollBehavior: () => ({ top: 0 }),
});

/**
 * A NAVEGAÇÃO ACENDE A BARRA DO TOPO — registrada antes de qualquer guarda,
 * porque as guardas são o que demora: restaurar a sessão, perguntar à plataforma,
 * ler os módulos do workspace.
 *
 * Um fecho por navegação, e não um contador: o vue-router NÃO chama `afterEach`
 * para uma navegação que uma guarda redirecionou (só para a final), nem para uma
 * que rebentou (vai para `onError`). Contando um `before` por `after`, cada
 * redirecionamento prenderia a barra acesa para sempre. Um redirecionamento
 * encontra o fecho ainda aberto e não abre outro.
 *
 * `cancelled` é ignorado: quer dizer que uma navegação mais nova já está a
 * correr, e é ela quem vai fechar. `aborted` (uma guarda devolveu `false` — a
 * recarga pós-deploy) e `duplicated` fecham sem vermelho: ninguém falhou.
 *
 * O `import()` da tela NÃO é coberto aqui — `lazyPage` resolve a rota na hora e
 * carrega o módulo depois do commit; ele acende a barra por conta própria.
 */
let endNavigation: ((failed?: boolean) => void) | null = null;

router.beforeEach(() => {
	endNavigation ??= beginLoading("route");
	return true;
});

router.afterEach((_to, _from, failure) => {
	if (isNavigationFailure(failure, NavigationFailureType.cancelled)) return;
	endNavigation?.();
	endNavigation = null;
});

// Só a rota final, já confirmada: o link do manifesto segue onde a pessoa está.
router.afterEach((to, _from, failure) => {
	if (!failure) syncManifestLink(to.path);
});

router.onError(() => {
	endNavigation?.(true);
	endNavigation = null;
});

/**
 * A RECARGA DEPOIS DO DEPLOY — antes de qualquer outra guarda, e é a primeira
 * registrada de propósito: as guardas correm na ordem em que foram escritas, e
 * não faz sentido restaurar sessão ou consultar acesso de plataforma para uma
 * navegação que vai ser descartada no próximo instante.
 *
 * `newDeployIsWaiting()` é levantado por `deploy-watch.ts` quando o servidor
 * passa a publicar outra versão. Aqui isso vira o único gesto que cura: trocar a
 * navegação de rota por um carregamento de página inteira NO MESMO DESTINO. A
 * pessoa clicou em "Conversas" e chega em Conversas — com o código novo. Ela não
 * vê nada além de um clique que demorou um pouco mais.
 *
 * `from.name` vazio é o boot: aí não há o que recarregar, o navegador acabou de
 * buscar o `index.html`. Sem essa condição, um carimbo novo lido no primeiro
 * tique recarregaria a aba que acabou de abrir.
 *
 * `return false` é o que impede a navegação de seguir enquanto o navegador começa
 * a sair da página — sem ele, as guardas de baixo ainda correriam e a tela de
 * destino poderia até montar, para ser jogada fora meio segundo depois.
 */
router.beforeEach((to, from) => {
	if (from.name && newDeployIsWaiting()) {
		window.location.href = to.fullPath;
		return false;
	}
	return true;
});

/**
 * One guard, and it has to handle the RESTORE.
 *
 * A refresh arrives with no identity in memory but a valid refresh token in the
 * browser, so redirecting on "no identity" alone would bounce everyone to the
 * sign-in screen on every reload. The guard waits for the restore to finish
 * first, which is the whole subtlety here.
 */
let restored: Promise<unknown> | null = null;

router.beforeEach(async (to) => {
	/**
	 * O PORTAL DO PARCEIRO SAI ANTES DE TUDO.
	 *
	 * Antes de `to.meta.public`, antes de `restoreSession()`, antes de qualquer
	 * leitura da identidade do tenant — e é essa ordem que é a regra. Uma sessão de
	 * tenant restaurada numa rota de parceiro seria a primeira frincha da parede
	 * que separa os dois mundos: o token do portal é opaco e vive sob outro prefixo
	 * no Redis, a sessão dele não tem `tenantId` nenhum, e a V2 rachou exatamente
	 * aqui — dois mundos com o mesmo segredo, separados por um literal no payload.
	 *
	 * `partnerGuardResult` é do lado do parceiro e decide sozinho: `true` passa, ou
	 * devolve para onde mandar (a tela de entrar, com o destino na query).
	 */
	// Em TODOS os caminhos deste guarda a pergunta "há sessão de tenant?" acaba
	// respondida, e é isso que o cliente HTTP espera antes de mandar um pedido sem
	// token. Numa rota de parceiro ou pública a resposta é "não há, e não vai
	// haver" — dizê-la é o que impede um pedido de ficar pendurado à espera de uma
	// restauração que ninguém vai fazer.
	if (to.meta.partner === true) {
		markSessionSettled();
		return partnerGuardResult(to);
	}

	if (to.meta.public) {
		markSessionSettled();
		return true;
	}

	const { identity } = useSessionState();
	if (!identity.value) {
		restored ??= restoreSession();
		await restored;
	}
	markSessionSettled();

	// `redirect` so signing in returns to where they were going, rather than
	// dropping them at the start and making them navigate again.
	if (!identity.value) {
		return { name: "sign-in", query: to.fullPath === "/" ? {} : { redirect: to.fullPath } };
	}

	/**
	 * The DEV routes need the platform flag, and it is asked for HERE rather than
	 * trusted from the nav. The server refuses these endpoints anyway; this stops
	 * someone typing the address and landing on a screen of empty panels and
	 * failed requests, which reads as a bug rather than as "not for you".
	 */
	if (to.meta.platformAdmin === true && !(await isPlatformAdmin())) {
		return { name: "home", query: { motivo: "so-plataforma", de: to.fullPath } };
	}

	// The same idea for the workspace role, and synchronous: the role arrived with
	// the identity, so there is nothing to ask the server for. Home rather than a
	// refusal screen — the person is not lost, they are just not an admin.
	// O `motivo` viaja junto porque a home é a mesma para os três desvios: sem ele,
	// "não é para o seu papel" e "esse endereço não existe" chegavam iguais.
	const minRole = to.meta.minRole as Role | undefined;
	if (minRole && !can(minRole)) {
		return { name: "home", query: { motivo: "so-admin", de: to.fullPath } };
	}

	const module = moduleForPath(to.path);
	if (module) {
		const access = await request<{ disabledModules: AccessModule[] }>("/v3/identity/access/me");
		if (access.disabledModules?.includes(module))
			return { name: "home", query: { motivo: "modulo-restrito", de: to.fullPath } };
	}
	return true;
});

/**
 * Whether this session administers the installation.
 *
 * Cached for the session: the guard runs on every navigation and the answer
 * changes about never. The SERVER re-checks it against the database on every
 * platform request, which is where revocation has to be immediate — this is
 * only about which screens to offer.
 */
let platformAdmin: boolean | null = null;

async function isPlatformAdmin(): Promise<boolean> {
	if (platformAdmin !== null) return platformAdmin;
	platformAdmin = await readPlatformAccess();
	return platformAdmin;
}
